import Constants from "const/Constants";
import Countries from "const/Countries";
import Currencies from "const/Currencies";
import DataStatuses from "const/DataStatuses";
import DocumentPaymentTypes from "const/DocumentPaymentTypes";
import Money from "utils/Money";

const MAX_LINE_ITEMS_AMOUNT_DIFFERENCE = 0.02;

const MAX_KWD_LINE_ITEMS_AMOUNT_DIFFERENCE = 0.001;

export default class Documents {
  static getAmountData(amountVatRates, amountBase, amountVat, amountCorrection = 0) {
    const defaultAmountVatRates = [{ rate: 0, base: (+amountBase || 0) + (+amountVat || 0), value: 0 }];

    amountVatRates = (amountVatRates || []).length ? amountVatRates : defaultAmountVatRates;

    const totalBaseValue = amountVatRates.reduce((aggregator, currentValue) => aggregator + currentValue.base || 0, 0);

    const totalVatValue = amountVatRates.reduce((aggregator, currentValue) => aggregator + currentValue.value || 0, 0);

    return { base: totalBaseValue, vat: totalVatValue, total: totalBaseValue + totalVatValue + amountCorrection };
  }

  static getDetailedTaxRates(paymentType, lineItems = [], currency) {
    const precision = Money.getCurrencyPrecision(currency);

    return Object.values(lineItems
      .map(({
        amountBase,
        taxRate: { rateDetails: { purchase = [], sales = [] } = {} } = {}
      }) => {
        const details = paymentType === DocumentPaymentTypes.BUY ? purchase : sales;

        amountBase = amountBase || 0;

        return details.map(({ id, rate, name }) => ({
          id,
          rate,
          name,
          value: Money.toMoneyNumber(amountBase * rate / Constants.PERCENTS_MULTIPLIER, true, precision),
          taxableAmount: amountBase
        }));
      })
      .flat()
      .reduce((aggregator, taxRateDetails) => {
        const currentValue = (aggregator[taxRateDetails.id] || {}).value || 0;

        const currentTaxableAmount = (aggregator[taxRateDetails.id] || {}).taxableAmount || 0;

        return {
          ...aggregator,
          [taxRateDetails.id]: {
            ...taxRateDetails,
            value: Money.toMoneyNumber(currentValue + taxRateDetails.value, true, precision),
            taxableAmount: Money.toMoneyNumber(currentTaxableAmount + taxRateDetails.taxableAmount, true, precision)
          }
        };
      }, {}))
      .sort((valueA, valueB) => valueA.rate - valueB.rate);
  }

  static getNonEmptyLineItems(lineItems) {
    return lineItems.filter((lineItem) => lineItem.description?.trim() !== "");
  }

  static calculateLineItemsData({ lineItems, paymentType, countryCode, quickBooksBusiness, currency }) {
    lineItems = Documents.getNonEmptyLineItems(lineItems);

    const usCountry = countryCode === Countries.US;

    const precision = Money.getCurrencyPrecision(currency);

    const detailedTaxRates = quickBooksBusiness && !usCountry && Documents.getDetailedTaxRates(paymentType, lineItems, currency);

    const rateDrivenNetRows = Documents.checkRateDrivenNetRows(lineItems, currency);

    const amountBase = Money.toMoneyNumber(
      lineItems.reduce((aggregator, item) => aggregator + (item.amountBase || 0), 0), true, precision
    );

    const amountVat = detailedTaxRates
      ? Money.toMoneyNumber(
        detailedTaxRates.reduce((aggregator, { value }) => {
          return aggregator + (value || 0);
        }, 0), true, precision
      )
      : Money.toMoneyNumber(
        lineItems.reduce((aggregator, item) => {
          const amount = item.taxRate?.id
            ? (item.amountBase || 0) * (item.taxRate.rate / Constants.PERCENTS_MULTIPLIER)
            : (item.amountVat || 0);

          return aggregator + (rateDrivenNetRows ? Money.toMoneyNumber(amount, true, precision) : amount);
        }, 0), true, precision
      );

    return {
      amountBase,
      amountVat,
      amount: Money.toMoneyNumber(amountBase + amountVat, true, precision),
      detailedTaxRates: detailedTaxRates || undefined,
      taxesHash: lineItems.map((item) => item.taxRate?.id).filter(Boolean).join()
    };
  }

  static checkRateDrivenNetRows(lineItems, currency) {
    lineItems = Documents.getNonEmptyLineItems(lineItems);

    const precision = Money.getCurrencyPrecision(currency);

    return lineItems.length > 0 && lineItems.every((lineItem) => {
      return lineItem.taxRate?.id && !lineItem.adjustment && !lineItem.default
        && [lineItem.amountBase, lineItem.amountVat, lineItem.amountTotal].every((value) => typeof value === "number")
        && Money.toMoneyNumber(lineItem.amountVat, true, precision) === 0
        && Money.toMoneyNumber(lineItem.amountTotal - lineItem.amountBase, true, precision) === 0;
    });
  }

  static checkAmountDataConsistence({ documentData, countryCode, vatPayer, quickBooksBusiness }) {
    let { amountBase, amountVat, amountVatRates = [], paymentType, lineItems } = documentData;

    const precision = Money.getCurrencyPrecision(documentData.currency);

    amountBase = Money.toMoneyNumber(amountBase || 0, true, precision);
    amountVat = Money.toMoneyNumber(amountVat || 0, true, precision);
    if (countryCode === Countries.CZ) {
      if (documentData.status !== DataStatuses.TO_REVIEW) return true;

      const invalidAmountBase = amountBase === "" || isNaN(+amountBase) || +amountBase < 0;

      if (invalidAmountBase) return false;

      const amount = Money.toMoneyNumber(amountBase + amountVat, true, precision);

      if (!vatPayer) return amountBase === amount;

      const totalBaseValue = amountVatRates.reduce((aggregator, currentValue) => aggregator + currentValue.base || 0, 0);

      const totalVatValue = amountVatRates.reduce((aggregator, currentValue) => aggregator + currentValue.value || 0, 0);

      const totalValue = Money.toMoneyNumber(totalBaseValue + totalVatValue, true, precision);

      return totalValue === amount;
    }

    const lineItemsData = Documents.calculateLineItemsData({
      lineItems, paymentType, countryCode, quickBooksBusiness, currency: documentData.currency
    });

    return amountBase === lineItemsData.amountBase
        && (!!lineItemsData.detailedTaxRates?.length || amountVat === lineItemsData.amountVat);
  }

  static checkLineItemsTotalConsistence(documentData) {
    const { currency, amountBase, amountVat, amount, paymentType } = documentData;

    const lineItems = Documents.getNonEmptyLineItems(documentData.lineItems);

    if (!lineItems.length) return true;

    const precision = Money.getCurrencyPrecision(currency);

    const maxDifference = currency === Currencies.KWD
      ? MAX_KWD_LINE_ITEMS_AMOUNT_DIFFERENCE : MAX_LINE_ITEMS_AMOUNT_DIFFERENCE;

    if (lineItems.some((lineItem) => {
      return Math.abs(Money.toMoneyNumber((lineItem.amountBase || 0) + (lineItem.amountVat || 0)
        - (lineItem.amountTotal || 0), true, "....")) > maxDifference;
    })) return false;
    if (Documents.checkRateDrivenNetRows(lineItems, currency)) {
      const codedTaxes = lineItems.map((lineItem) => {
        if (lineItem.taxRate.rateDetails) {
          const details = Documents.getDetailedTaxRates(paymentType, [lineItem], currency);

          return details.length ? details.reduce((sum, { value }) => sum + value, 0) : null;
        }

        return typeof lineItem.taxRate.rate === "number"
          ? Money.toMoneyNumber(lineItem.amountBase * lineItem.taxRate.rate / Constants.PERCENTS_MULTIPLIER, true, precision)
          : null;
      });

      const lineItemsAmountBase = lineItems.reduce((sum, lineItem) => sum + lineItem.amountBase, 0);

      if (codedTaxes.every((value) => value !== null && !isNaN(value))
        && Math.abs(Money.toMoneyNumber(lineItemsAmountBase - amountBase, true, "....")) <= maxDifference
        && Math.abs(Money.toMoneyNumber(
          codedTaxes.reduce((sum, value) => sum + value, 0) - amountVat,
          true,
          "...."
        )) <= maxDifference
        && Math.abs(Money.toMoneyNumber(amountBase + amountVat - amount, true, "....")) <= maxDifference) return true;
    }

    return Math.abs(Money.toMoneyNumber(
      lineItems.reduce((sum, lineItem) => sum + (lineItem.amountTotal || 0), 0) - (amount || 0), true, "...."
    )) <= maxDifference;
  }
}
